/**
 * cart-upgrades.js
 * UPGRADES inline dans le cart drawer.
 * Affiche une carte sous la ligne du produit à remplacer.
 * Séparé de cart-upsells.js pour plus de clarté.
 *
 * Structure liquid requise dans cart-items.liquid (déjà en place) :
 *   <tr data-upsell-slot data-upsell-parent-variant="{{ item.variant.id }}">
 *     <td colspan="2">
 *       <div class="cart-item-upsell-placeholder"></div>
 *     </td>
 *   </tr>
 */
(function () {
  "use strict";

  const SEL_SLOT = "[data-upsell-slot]";

  let _debounce = null;
  let _runId    = 0;

  // ─── UTILITAIRES ─────────────────────────────────────────────

  function scheduleRefresh(ms) {
    clearTimeout(_debounce);
    _debounce = setTimeout(refresh, ms ?? 80);
  }

  async function fetchCart() {
    const r = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    if (!r.ok) throw new Error("cart.js " + r.status);
    return r.json();
  }

  // form-encoded (évite Clear-Site-Data)
  async function cartAdd(variantId) {
    const body = new URLSearchParams({ id: String(variantId), quantity: "1" });
    const r = await fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
      body,
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.description || d.message || "Ajout impossible");
    }
    return r.json();
  }

  async function cartRemove(variantIds) {
    if (!variantIds.length) return;
    const updates = {};
    variantIds.forEach(id => { updates[String(id)] = 0; });
    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates }),
    });
  }

  // Re-render ciblé : items + footer + bubble
  async function rerenderDrawer() {
    const r = await fetch("/cart?sections=cart-drawer", {
      headers: { Accept: "application/json" },
    });
    if (!r.ok) return;
    const sections = await r.json();
    const html = sections["cart-drawer"];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, "text/html");

    ["#CartDrawer-CartItems", ".drawer__footer", "#cart-icon-bubble"].forEach(sel => {
      const n = doc.querySelector(sel);
      const o = document.querySelector(sel);
      if (n && o) o.innerHTML = n.innerHTML;
    });
  }

  async function fetchCard(handle, variantId) {
    const r = await fetch(`/products/${handle}?view=upsell-card&variant=${variantId}`);
    if (!r.ok) throw new Error("card 404: " + handle);
    const text = await r.text();
    const tmp = document.createElement("div");
    tmp.innerHTML = text.trim();
    const card = tmp.querySelector("[data-upsell-card]");
    return card ? card.outerHTML : null;
  }

  // ─── RÈGLES ──────────────────────────────────────────────────

  function csv(val) {
    return String(val || "").split(",").map(s => s.trim()).filter(Boolean);
  }

  function getRules() {
    return (window.MOODZ_UPSELL_RULES || [])
      .filter(r => r.upsellHandle && r.upsellVariantId && r.replaceVariantId)
      .map(r => ({
        handle:      String(r.upsellHandle).trim(),
        variantId:   String(r.upsellVariantId).trim(),
        replaceIds:  csv(r.replaceVariantId),  // variantes à remplacer (déclenchent ET suppriment)
        excludeIds:  csv(r.excludeVariantId),
        // styles
        cardBg:      r.cardBg             || "",
        cardRadius:  Number(r.cardRadius) || 0,
        borderW:     Number(r.cardBorderWidth) || 0,
        borderC:     r.cardBorderColor    || "",
        imageUrl:    r.imageOverride      || "",
        customTitle: r.customTitle        || "",
        priceText:   r.customPriceMain    || "",
        compareText: r.customPriceCompare || "",
        priceColor:  r.priceMainColor     || "",
        compareColor:r.priceCompareColor  || "",
        btnLabel:    r.buttonLabel        || "",
        btnBg:       r.buttonBg           || "",
        btnColor:    r.buttonTextColor    || "",
        btnRadius:   Number(r.buttonRadius)      || 0,
        btnBorderW:  Number(r.buttonBorderWidth) || 0,
        btnBorderC:  r.buttonBorderColor         || "",
      }));
  }

  // ─── FILTRE ───────────────────────────────────────────────────

  function filterRules(rules, cartItems) {
    const qty = {};
    cartItems.forEach(item => {
      qty[String(item.id)] = (qty[String(item.id)] || 0) + item.quantity;
    });
    const inCart = id => (qty[String(id)] || 0) > 0;

    return rules.filter(rule => {
      // L'upgrade s'affiche si AU MOINS UN replaceId est dans le panier
      if (!rule.replaceIds.some(id => inCart(id))) return false;
      // Masquer si l'upsell est déjà dans le panier
      if (inCart(rule.variantId)) return false;
      // Excludes
      if (rule.excludeIds.some(id => inCart(id))) return false;
      return true;
    });
  }

  // ─── STYLES ───────────────────────────────────────────────────

  function applyStyle(cardEl, rule) {
    if (!cardEl) return;
    if (rule.cardBg)     cardEl.style.backgroundColor = rule.cardBg;
    if (rule.cardRadius) cardEl.style.borderRadius     = rule.cardRadius + "px";
    cardEl.style.borderWidth = (rule.borderW || 0) + "px";
    cardEl.style.borderStyle = rule.borderW > 0 ? "solid" : "";
    if (rule.borderC)    cardEl.style.borderColor = rule.borderC;

    const img = cardEl.querySelector("[data-upsell-image-wrapper] img");
    if (img && rule.imageUrl) img.src = rule.imageUrl;

    const title = cardEl.querySelector("[data-upsell-title]");
    if (title && rule.customTitle) title.textContent = rule.customTitle;

    const price = cardEl.querySelector("[data-upsell-price-main]");
    if (price) {
      if (rule.priceText) price.textContent = rule.priceText.replace("{price}", price.textContent.trim());
      if (rule.priceColor) price.style.setProperty("color", rule.priceColor, "important");
    }

    const compare = cardEl.querySelector("[data-upsell-price-compare]");
    if (compare) {
      if (rule.compareText) compare.textContent = rule.compareText.replace("{compare_price}", compare.textContent.trim());
      if (rule.compareColor) compare.style.setProperty("color", rule.compareColor, "important");
    }

    const btn = cardEl.querySelector("[data-upsell-button]");
    if (btn) {
      if (rule.btnLabel)  btn.textContent        = rule.btnLabel;
      if (rule.btnBg)     btn.style.background   = rule.btnBg;
      if (rule.btnColor)  btn.style.color        = rule.btnColor;
      if (rule.btnRadius) btn.style.borderRadius = rule.btnRadius + "px";
      btn.style.borderWidth = (rule.btnBorderW || 0) + "px";
      btn.style.borderStyle = rule.btnBorderW > 0 ? "solid" : "";
      if (rule.btnBorderC) btn.style.borderColor = rule.btnBorderC;
    }
  }

  // ─── REFRESH ─────────────────────────────────────────────────

  async function refresh() {
    const slots = Array.from(document.querySelectorAll(SEL_SLOT));

    // Vider tous les slots d'abord
    slots.forEach(slot => {
      const ph = slot.querySelector(".cart-item-upsell-placeholder");
      if (ph) ph.innerHTML = "";
      slot.hidden = true;
    });

    if (!slots.length) return;

    const runId = ++_runId;

    try {
      const cart   = await fetchCart();
      if (runId !== _runId) return;

      const rules  = getRules();
      const active = filterRules(rules, cart.items || []);

      if (!active.length) return;

      // Fetch les cartes en parallèle
      const htmlParts = await Promise.all(
        active.map(r => fetchCard(r.handle, r.variantId).catch(() => null))
      );
      if (runId !== _runId) return;

      active.forEach((rule, i) => {
        const html = htmlParts[i];
        if (!html) return;

        // Trouver le slot dont data-upsell-parent-variant matche un des replaceIds
        const targetSlot = slots.find(slot =>
          rule.replaceIds.includes(slot.dataset.upsellParentVariant)
        );
        if (!targetSlot) return;

        const placeholder = targetSlot.querySelector(".cart-item-upsell-placeholder");
        if (!placeholder) return;

        const tmp = document.createElement("div");
        tmp.innerHTML = html;
        applyStyle(tmp.querySelector("[data-upsell-card]"), rule);

        placeholder.innerHTML = `
          <div class="cart-item-upsell-inline">
            <div class="svg-arrow-upgrade bounce2">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32.34 42.32">
                <path d="M27.88,20.87v22H11.17v-22H4c-.25,0-.47,0-.58-.26a.6.6,0,0,1,.13-.69L7.89,14.5,17.32,2.79c.54-.67,1.09-1.34,1.64-2,.28-.34.52-.37.8,0,.78.89,1.53,1.8,2.28,2.71l1.87,2.28L35.47,19.88c.29.36.31.61,0,.85a.67.67,0,0,1-.42.12l-6.83,0Z" transform="translate(-3.36 -0.52)"/>
              </svg>
            </div>
            ${tmp.innerHTML}
          </div>`;
        targetSlot.hidden = false;
      });

    } catch (err) {
      console.error("[UPSELL UPGRADE] refresh:", err);
    }
  }

  // ─── ATC ─────────────────────────────────────────────────────

  document.addEventListener("click", async function (e) {
    const btn = e.target.closest("[data-upsell-add-to-cart]");
    if (!btn) return;
    // Seulement les boutons dans un slot upgrade
    if (!btn.closest(SEL_SLOT)) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    // Retrouver la règle pour savoir quoi supprimer
    const rules = getRules();
    const rule  = rules.find(r => r.variantId === String(variantId));
    if (!rule) return;

    btn.disabled = true;
    btn.classList.add("is-loading");

    try {
      // 1. Supprimer les variantes à remplacer
      await cartRemove(rule.replaceIds);

      // 2. Ajouter l'upgrade
      await cartAdd(variantId);

      // 3. Re-render drawer (items + footer)
      await rerenderDrawer();

      // 4. Refresh les upgrades (les slots sont dans le nouveau DOM)
      requestAnimationFrame(() => scheduleRefresh(150));

      // 5. Notifier les upsells payants qu'il faut se rafraîchir aussi
      if (typeof window.MOODZ_UPSELL_REFRESH === "function") {
        setTimeout(window.MOODZ_UPSELL_REFRESH, 300);
      }

    } catch (err) {
      console.error("[UPSELL UPGRADE] ATC:", err);
      const card = btn.closest("[data-upsell-card]");
      let msg = card?.querySelector("[data-upsell-msg]");
      if (!msg && card) {
        msg = document.createElement("p");
        msg.dataset.upsellMsg = "";
        msg.style.cssText = "color:red;font-size:11px;margin:4px 0 0;text-align:center";
        card.appendChild(msg);
      }
      if (msg) {
        msg.textContent = err.message || "Erreur lors de l'ajout";
        setTimeout(() => { msg.textContent = ""; }, 4000);
      }
    } finally {
      btn.disabled = false;
      btn.classList.remove("is-loading");
    }
  });

  // ─── LISTENERS ───────────────────────────────────────────────

  function listenPubSub() {
    if (typeof subscribe === "function" && window.PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleRefresh(120));
    } else {
      setTimeout(listenPubSub, 300);
    }
  }

  // Patch renderContents : après re-render du drawer, les slots sont
  // recréés par le liquid → on relance refresh pour les repeupler
  function patchDrawer() {
    window.customElements?.whenDefined?.("cart-drawer").then(() => {
      const D = customElements.get("cart-drawer");
      if (!D || D.prototype.__moodz_upgrade_patched) return;
      D.prototype.__moodz_upgrade_patched = true;
      const orig = D.prototype.renderContents;
      if (typeof orig !== "function") return;
      D.prototype.renderContents = function (state) {
        const result = orig.call(this, state);
        setTimeout(() => scheduleRefresh(200), 400);
        return result;
      };
    });
  }

  // ─── INIT ─────────────────────────────────────────────────────

  window.MOODZ_UPGRADE_REFRESH = () => scheduleRefresh(0);

  document.addEventListener("DOMContentLoaded", () => {
    patchDrawer();
    listenPubSub();
    scheduleRefresh(0);
  });

  document.addEventListener("shopify:section:load", () => scheduleRefresh(0));

})();